/**
 * Card Deck Fan — 5 brand cards fan out from center, cycled glow on each.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem } from "./_mockHelpers.js";

export const cardDeckFan: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Lokal|aus deiner Stadt|📍", "Persönlich|Direkt-Chat|💬", "Sicher|Käuferschutz|🛡", "Fair|0% Provision|⚖", "Schnell|Same-Day|⚡"]).slice(0, 5);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const isVertical = ctx.format === "9:16";

  const cardW = isVertical ? 240 : 260;
  const cardH = Math.round(cardW * 1.45);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const fanId = `${clipPrefix}-fan`;
  const cardIds = items.map((_, i) => `${clipPrefix}-c-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const centerIndex = (items.length - 1) / 2;
  const spread = isVertical ? 22 : 28;

  const cards = items.map((raw, i) => {
    const [title, desc, icon] = splitItem(raw);
    const offset = i - centerIndex;
    const rot = offset * spread * 0.4;
    const x = offset * (cardW * 0.42);
    const y = Math.abs(offset) * 10;
    return `<div id="${cardIds[i]}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) translate(${x}px, ${y}px) rotate(${rot}deg);width:${cardW}px;height:${cardH}px;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};border-radius:28px;padding:24px;display:flex;flex-direction:column;justify-content:space-between;box-shadow:0 30px 70px rgba(0,0,0,0.45), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.12)};backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);">
      <div style="width:64px;height:64px;border-radius:18px;background:linear-gradient(140deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 100%);display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-size:32px;box-shadow:0 0 24px ${withAlpha(accent, 0.55)};">${escapeHtml(icon ?? "★")}</div>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.5}px;letter-spacing:-0.5px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(title ?? "")}</div>
        ${desc ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.62}px;color:${withAlpha(themeColors.fg, 0.7)};line-height:1.35;">${escapeHtml(desc)}</div>` : ""}
      </div>
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:40px;padding:64px;">
    <div style="display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center;max-width:1080px;">
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
      ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.95}px;color:${withAlpha(themeColors.fg, 0.78)};max-width:760px;">${escapeHtml(sub)}</div>` : ""}
    </div>
    <div id="${fanId}" style="position:relative;width:100%;height:${cardH + 60}px;">${cards}</div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.4, rotation: 0 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.0));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.15));
  cardIds.forEach((id, i) => {
    const offset = i - centerIndex;
    tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, rotation: offset * spread * 0.4, duration: 0.7, ease: "raw:\"back.out(1.4)\"" }, 0.4 + i * 0.1));
  });
  // Cyclic highlight
  const cycle = Math.max(0.7, (clipDurationSec - 0.6) / Math.max(items.length, 1));
  cardIds.forEach((id, i) => {
    const at = 1.2 + i * cycle * 0.6;
    tweens.push(t.to(`#${id}`, { y: -16, boxShadow: `0 30px 90px ${withAlpha(accent, 0.65)}, 0 0 40px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.14)}`, duration: 0.4, ease: "raw:\"power2.out\"" }, at));
    tweens.push(t.to(`#${id}`, { y: 0, boxShadow: `0 30px 70px rgba(0,0,0,0.45), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.12)}`, duration: 0.4, ease: "raw:\"power2.inOut\"" }, at + 0.6));
  });
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
